import { useEffect } from 'react';

// Instant pressed state for every tappable thing on a dashboard (buttons, links, tab buttons,
// and list rows / cards that use an inline cursor: pointer), without per-screen code. While
// pressed the element is dimmed through its inline opacity -- no transform, size or border
// change, so nothing shifts -- and it stays dimmed at least MIN_MS so a quick tap still shows.
// A press that turns into a scroll is cancelled by the browser (pointercancel) and restored.
// It also registers a no-op touchstart listener: iOS Safari only applies :active on touch when
// one exists, which the global button:active style in index.css relies on.
const DIM = 0.8;
const MIN_MS = 100;
const TAPPABLE = 'button, a[href], [role="button"], [role="tab"], input[type="button"], input[type="submit"]';

function findTarget(node) {
  for (let el = node; el && el !== document.body; el = el.parentElement) {
    if (!(el instanceof HTMLElement)) continue;
    if (el.closest('[data-no-press]')) return null;
    if (el.matches(TAPPABLE) || el.style.cursor === 'pointer') {
      if (el.disabled || el.getAttribute('aria-disabled') === 'true') return null;
      return el;
    }
  }
  return null;
}

export function usePressFeedback() {
  useEffect(() => {
    let pressed = null; // { el, prevOpacity, dimmedTo, at }

    const release = () => {
      if (!pressed) return;
      const { el, prevOpacity, dimmedTo, at } = pressed;
      pressed = null;
      const restore = () => {
        // Only undo our own change: if the app set a new opacity meanwhile (e.g. a loading
        // state), leave that alone.
        if (el.style.opacity === dimmedTo) el.style.opacity = prevOpacity;
      };
      const left = MIN_MS - (Date.now() - at);
      if (left > 0) setTimeout(restore, left); else restore();
    };

    const onDown = (e) => {
      if (e.button > 0) return;
      release();
      const el = findTarget(e.target);
      if (!el) return;
      const prevOpacity = el.style.opacity;
      const base = parseFloat(getComputedStyle(el).opacity);
      const dimmedTo = String(Math.round((Number.isFinite(base) ? base : 1) * DIM * 100) / 100);
      el.style.opacity = dimmedTo;
      pressed = { el, prevOpacity, dimmedTo: el.style.opacity, at: Date.now() };
    };

    const noop = () => {};
    document.addEventListener('touchstart', noop, { passive: true });
    document.addEventListener('pointerdown', onDown, { passive: true });
    document.addEventListener('pointerup', release, { passive: true });
    document.addEventListener('pointercancel', release, { passive: true });
    window.addEventListener('blur', release);
    return () => {
      release();
      document.removeEventListener('touchstart', noop);
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('pointerup', release);
      document.removeEventListener('pointercancel', release);
      window.removeEventListener('blur', release);
    };
  }, []);
}
